import { InputNumber, Select as AntSelect } from 'antd'
import classNames from 'classnames'
import type { FC } from 'react'

import type { PunctualityItem, PunctualityType } from 'features/Home/interfaces/IInfoSchedule'
import type { PatientBufferTime } from 'features/Schedule/api/schedule.types'

import styles from './ehrAppointmentV2.module.scss'

type Props = {
  isOpen: boolean
  punctuality: PunctualityType[]
  patientPunctuality: PunctualityItem | null
  typeCode: string | number
  minutes: number
  bufferTime: PatientBufferTime
  disabled: boolean
  error?: string
  onToggle: () => void
  onTypeChange: (value: string) => void
  onMinutesChange: (value: number) => void
  onReset: () => void
}

const ON_TIME = '0'

const typeName = (punctuality: PunctualityType[], code: number | string): string =>
  punctuality.find((item) => String(item.code) === String(code))?.name ?? 'On Time'

export const SmsStrip: FC<Props> = ({
  isOpen,
  punctuality,
  patientPunctuality,
  typeCode,
  minutes,
  bufferTime,
  disabled,
  error,
  onToggle,
  onTypeChange,
  onMinutesChange,
  onReset,
}) => {
  const currentType = String(typeCode ?? ON_TIME)
  const isOverridden = currentType !== ON_TIME || minutes !== 0
  const isEditable = currentType !== ON_TIME
  const maxMinutes = currentType === '1' ? bufferTime.after : bufferTime.before

  const accountLine = patientPunctuality
    ? `Arrive ${patientPunctuality.minutes} minutes · ${typeName(
        punctuality,
        patientPunctuality.code,
      ).toLowerCase()}`
    : 'Not set for this patient'

  const summary = isEditable
    ? `Arrive ${minutes} min ${typeName(punctuality, currentType).toLowerCase()} appt time`
    : 'Arrive at the appointment time'

  return (
    <div className={styles.smsBand}>
      <button type='button' className={styles.smsHeader} onClick={onToggle}>
        <span className={styles.smsCaret}>{isOpen ? '▾' : '▸'}</span>
        <span className={styles.smsTitle}>SMS options</span>
        <span className={styles.smsSummary}>{summary}</span>
        <span className={styles.fieldGrow} />
        <span className={styles.resultsNote}>
          {isOverridden ? 'Appointment override' : 'Following account settings'}
        </span>
        {error && !isOpen && <span className={styles.errorText}>{error}</span>}
      </button>

      {isOpen && (
        <div className={styles.smsBody}>
          <div className={styles.smsAccount}>
            <span className={styles.smsKicker}>ACCOUNT DEFAULT</span>
            <span className={styles.smsRow}>{accountLine}</span>
            <span className={styles.resultsNote}>Applies when this appointment sets nothing</span>
          </div>

          <div className={styles.smsAppointment}>
            <div className={styles.smsRow}>
              <span className={classNames(styles.smsKicker, styles.smsKickerAccent)}>
                THIS APPOINTMENT
              </span>
              {isOverridden && <span className={styles.badge}>OVERRIDES ACCOUNT</span>}
              <span className={styles.fieldGrow} />
              {isOverridden && (
                <button type='button' className={styles.linkButton} onClick={onReset}>
                  Reset to account
                </button>
              )}
            </div>

            <div className={styles.smsRow}>
              <span>Tell patient to arrive</span>
              <InputNumber
                aria-label='Arrive minutes'
                className={styles.smsMinutes}
                min={0}
                max={maxMinutes}
                step={5}
                value={minutes}
                disabled={disabled || !isEditable}
                onChange={(value) => onMinutesChange(value ?? 0)}
              />
              <span>minutes</span>
              <AntSelect
                aria-label='Arrive when'
                value={currentType}
                disabled={disabled}
                style={{ width: 132 }}
                options={punctuality.map((item) => ({
                  value: String(item.code),
                  label: item.name,
                }))}
                onChange={onTypeChange}
              />
              <span>appointment time</span>
            </div>

            {error && <span className={styles.errorText}>{error}</span>}
          </div>
        </div>
      )}
    </div>
  )
}
